import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { AdminService } from 'src/app/core/services/admin.service';
import { Account, User } from '@mitel/cloudlink-sdk';
import { IntegTwilioService } from './integ-twilio.service';
import { WorkflowService } from './workflow.service';
import { Util } from '../utils/Util';
import { DropdownItem } from 'src/app/shared/components/searchable-dropdown/searchable-dropdown.component';
import LRUCache from 'lru-cache';
import { TranslateService } from '@ngx-translate/core';
import { AccountsService } from './accounts.service';
import { SmsNumberOperation, UsersBulkEditSmsNumbersService } from './users-bulk-edit-sms-numbers.service';

const fileContext = 'IntegtwilioSmsNumbersService';

export interface TwilioSmsNumbersWorkflowIds {
    userTagChangeTrigger: string;
    userDeletionTrigger: string;
}

export interface TwilioSmsNumbersTag {
    enabled: boolean,
    workflowIds?: TwilioSmsNumbersWorkflowIds
}

export interface TwilioSmsNumbersGlobalWorkflowIds extends TwilioSmsNumbersWorkflowIds {
    getAvailableNumbers: string;
}

/**
 * On Accounts Integration page, this service is used to manage a Twilio subIntegration toggle option called
 * 'User Numbers', also known as 'Assignment of Twilio numbers to users for MiCollab 1:1 SMS Feature' https://mitel.atlassian.net/browse/CLSD-3477
 */
@Injectable({
    providedIn: 'root'
})    
export class IntegTwilioSmsNumbersService implements IntegrationItemServiceInf {

    readonly workflowGlobalIds: TwilioSmsNumbersGlobalWorkflowIds = {
        userTagChangeTrigger: 'global_1c681d40-4010-4e2e-999b-9d91dc128355',
        userDeletionTrigger: 'global_2e5caa9f-a7b8-4da9-9464-45964b524c0b',
        getAvailableNumbers: 'global_44eda875-1351-4d5d-be48-ccc0845e16d3',
    }

    /**
     * Cache for available Twilio SMS Numbers. This is to avoid calling Twilio API for every user form load.
     * 
     * Cached by the connectionId which is unique for each account and set on user form load.
     */
    private availableNumbersCache = new LRUCache<string, string[]>({
        ttl: 60 * 5 * 1000,  // ms (cache item expires in 5 min)
        max: 3               // and, oldest item in cache is bumped if more than this items in cache
        // https://github.com/isaacs/node-lru-cache#readme
    });
    private _connectionId: string;

    private _adminService: AdminService;
    private get adminService(): AdminService {
        this._adminService ??= AppInjector.injector.get(AdminService);
        return this._adminService;
    };

    private _accountService: AccountsService;
    private get accountService(): AccountsService {
        this._accountService ??= AppInjector.injector.get(AccountsService);
        return this._accountService;
    };

    private _integTwilioService: IntegTwilioService;
    private get integTwilioService(): IntegTwilioService {
        this._integTwilioService ??= AppInjector.injector.get(IntegTwilioService);
        return this._integTwilioService;
    };

    private _workflowService: WorkflowService;
    private get workflowService(): WorkflowService {
        this._workflowService ??= AppInjector.injector.get(WorkflowService);
        return this._workflowService;
    };

    private _translate: TranslateService;
    private get translate(): TranslateService {
        this._translate ??= AppInjector.injector.get(TranslateService);
        return this._translate;
    };

    private _usersBulkEditSmsNumbersService: UsersBulkEditSmsNumbersService;
    private get usersBulkEditSmsNumbersService(): UsersBulkEditSmsNumbersService {
        this._usersBulkEditSmsNumbersService ??= AppInjector.injector.get(UsersBulkEditSmsNumbersService);
        return this._usersBulkEditSmsNumbersService;
    };

    constructor() {
    }


    canBeSelected(account: Account): Promise<boolean> {
        return Promise.resolve(false);
    }

    isIntegrationStateChangeable(account: Account): Promise<boolean> {
        return Promise.resolve(true);
    }

    isIntegrationStateOn(account: Account): Promise<boolean> {
        return Promise.all([
            this.adminService.getAccountTags(account.accountId),
            this.integTwilioService.isIntegrationStateOn(account)
        ])
            .then(([tags, isTwilioEnabled]) => isTwilioEnabled && !!tags?.smsNumbersIntegration?.enabled)
            .catch(err => {
                console.error(`${fileContext}.isIntegrationStateOn Failed to get account tags`, err);
                return false;
            });
    }

    getConfigurationDialog(): any {
        return undefined;
    }

    isSetupComplete(account: Account): Promise<boolean> {
        return Promise.resolve(true);
    }

    handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any> {
        if (state) {
            return this.createIntegration(account.accountId);
        } else {
            return this.disableIntegration(account.accountId);
        }
    }

    // When the parent is removed, this is the method called so the child can clean up as well
    removeAsASubintegration(account: Account): Promise<any> {
        return this.deleteIntegration(account.accountId);
    }

    getStatusString(account: Account): Promise<StatusString> {
        return undefined;
    }

    getConfigButtonLabel(account: Account): Promise<string> {
        return undefined;
    }

    isButtonDisabled(account: Account): Promise<boolean> {
        return Promise.resolve(false);
    }

    canDeleteIntegration(account: Account): Promise<boolean> {
        // This is a subintegration. It is never deleted standalone
        return Promise.resolve(false); // should never be called, but just in case
    }

    private cloneWorkFlows() {
        const promises = Promise.allSettled([
            this.integTwilioService.cloneAndLockHideActivity(this.workflowGlobalIds.userDeletionTrigger, 'Cloned Twilio User Deletion Trigger Flow'),
            this.integTwilioService.cloneAndLockHideActivity(this.workflowGlobalIds.userTagChangeTrigger, 'Cloned Twilio User Change Trigger Flow'),
        ]);
        return promises.then(ret => {
            const rejected = ret.find(p => p.status === 'rejected')?.['reason']; // just grab the 1st error
            const result = {
                userDeletionTriggerFlow: ret[0].status === 'fulfilled' ? ret[0].value : undefined,
                userTagChangeTriggerFlow: ret[1].status === 'fulfilled' ? ret[1].value : undefined
            }
            if (rejected) {
                // cleanup
                try {
                    this.deleteWorkflows({ userDeletionTrigger: result.userDeletionTriggerFlow?.id, userTagChangeTrigger: result.userTagChangeTriggerFlow?.id })
                } catch (e) {
                    // Expected error because one or more of those workflow IDs are undefined
                    // RequiredError: Required parameter activityId was null or undefined when calling deleteActivityById.
                }
                throw new Error(`${fileContext}.cloneWorkFlows failed: ${Util.extractMessageFromError(rejected) || JSON.stringify(rejected)}`);
            }
            return result;
        });
    }
    
    private createIntegration(accountId: string): Promise<any> {
        // create Workflows
        // update account tags with workflow ids
        return Promise.all([
            this.adminService.getAccountTags(accountId),
            this.cloneWorkFlows()
        ]).then(([tags, flows]) => {
            const body: TwilioSmsNumbersTag = {
                enabled: true,
                workflowIds: {
                    userTagChangeTrigger: flows.userTagChangeTriggerFlow.id,
                    userDeletionTrigger: flows.userDeletionTriggerFlow.id
                }
            }
            if (tags?.smsNumbersIntegration) {
                return this.adminService.updateAccountTag(accountId, 'smsNumbersIntegration', body);
            }
            else {
                return this.adminService.createAccountTagNoFrills(accountId, { smsNumbersIntegration: body });
            }
        });
    }

    private deleteWorkflows(ids: TwilioSmsNumbersWorkflowIds) {
        const promises = [];
        if (ids?.userTagChangeTrigger) {
            promises.push(this.workflowService.deleteActivityById(ids.userTagChangeTrigger));
        }
        if (ids?.userDeletionTrigger) {
            promises.push(this.workflowService.deleteActivityById(ids.userDeletionTrigger));
        }
        return Promise.all(promises);
    }

    /**
     * - bulk unassign numbers from users
     * - delete workflows
     * - delete tag
     * @param accountId 
     * @returns 
     */
    private deleteIntegration(accountId: string): Promise<any> {
        return this.getListOfUsersWithSmsNumberAssigned(accountId)
            .then(users => {
                if (users?.length > 0) {
                    console.log(`${fileContext}.deleteIntegration Unassigning Twilio SMS Numbers from ${users.length} users`);
                    return this.usersBulkEditSmsNumbersService.saveUserTwilioSmsNumberBulkOperation(
                        accountId,
                        users,
                        SmsNumberOperation.unassignAll)
                }
            })
            .then(() => this.adminService.getAccountTags(accountId)
            .then(tags => {
                if (tags?.smsNumbersIntegration?.workflowIds) {
                    console.log(`${fileContext}.deleteIntegration Deleting Twilio SMS Numbers Workflows`, tags.smsNumbersIntegration.workflowIds);
                    return this.deleteWorkflows(tags.smsNumbersIntegration.workflowIds);
                } else {
                    console.log(`${fileContext}.deleteIntegration No Twilio SMS Numbers Workflows to delete`);
                    return;
                }
            })
            .then(() => {
                return this.adminService.deleteAccountTag(accountId, 'smsNumbersIntegration').catch(err => {
                    if (err.statusCode === 404) {
                        // an error is thrown if no tag exists. That is OK!
                        return;
                    }
                    console.error(`${fileContext}.deleteIntegration Failed to delete account tag`, err);
                    throw err;
                });
            }))
            .catch(err => {
                console.error(`${fileContext}.deleteIntegration Failed to delete integration`, err);
                throw err;
            });
    }

    /**
     * In account tags, will set the smsNumbersIntegration.enabled to false and delete any workflows
     * @param accountId 
     * @returns 
     */
    private disableIntegration(accountId: string): Promise<any> {
        return this.adminService.getAccountTags(accountId).then(tags => {
            const smsNumbersIntegration: TwilioSmsNumbersTag = tags?.smsNumbersIntegration;
            if (smsNumbersIntegration) {
                this.deleteWorkflows(smsNumbersIntegration.workflowIds).then(() => {
                    let updatedSmsNumbersIntegration: TwilioSmsNumbersTag = {
                        ...smsNumbersIntegration,
                        enabled: false,
                    };
                    delete updatedSmsNumbersIntegration.workflowIds;
                    return this.adminService.updateAccountTag(accountId, 'smsNumbersIntegration', updatedSmsNumbersIntegration);
                }).catch(err => {
                    console.error(`${fileContext}.disableIntegration Failed to delete workflows`, err);
                    throw err;
                });
            }
        });
    }

    async updateMiCollabSMSNumber(accountId: string, userId: string, existingTags: any, smsNumber: string) {
        if (!smsNumber || smsNumber == '') {
            return this.adminService.deleteUserTagByAccountIdWithoutSpinner(accountId, userId, 'smsIntegration').then(() => {
                let updatedTags = {...existingTags};
                delete updatedTags.smsIntegration;
                return updatedTags;
            });
        } else {
            let updatedTags = { smsIntegration: existingTags?.smsIntegration ? { ...existingTags.smsIntegration } : {} };
            updatedTags.smsIntegration['number'] = smsNumber.toString().trim();
            return this.adminService.tryUpdateTagsOfUserByAccountIdNoFrills(accountId, userId, updatedTags, existingTags);
        }
    }

    set connectionId(value: string) { // This is set each time the user form is loaded.
        this._connectionId = value;
    }

    public clearCache() {
        this.availableNumbersCache.clear();
    }

    // From https://github.com/mitel-networks/cloudlink-cc-admin-portal/blob/develop/src/app/modules/social/services/twilio.service.ts#L86
    // Modified to have the workflow do the paging and to only return available numbers
    private async getSmsNumbersFromTwilio(): Promise<string[]> {
        let body = {
            inputs: {
                Connection: {
                    expression: this._connectionId,
                    expressionType: 'Connection'
                },
                Route: 'Accounts/:accountId/IncomingPhoneNumbers.json',
                PageRecords: false
            }
        };

        // This will need to be adjusted to call the existing WF instead of the new one
        return this.workflowService.runWorkerByActivityId(this.workflowGlobalIds.getAvailableNumbers, body)
            .then(r => {
                return r.incoming_phone_numbers?.length
                    ? r.incoming_phone_numbers.reduce(
                        (availableNumbers, n) => {
                            if ("sms_url" in n && (!n.sms_url || n.sms_url.startsWith("https://demo.twilio.com"))) { // Only include numbers that are not in use
                                availableNumbers.push(n.phone_number); // Value added to tags must be exactly the same as n.phone_number
                            }
                            return availableNumbers;
                        }, [])
                    : []
            })
            .then(rawNumbers => rawNumbers.sort((a, b) => a - b) || []); // Sort the returned numbers from smallest to largest
    }

    public async getListOfAvailableSmsNumbers() {
        this._connectionId ??= await this.getTwilioConnectionIdFromTags(this.accountService.getCurrentAccount().accountId);
        if (!this._connectionId) { // This should never happen, but just in case
            console.error(`${fileContext}.getListOfAvailableSmsNumbers: connectionId is not set`);
            return [];
        }
        let numbers: string[];
        if (this.availableNumbersCache.has(this._connectionId)) {
            numbers = this.availableNumbersCache.get(this._connectionId);
        } else {
            numbers = await this.getSmsNumbersFromTwilio();
            console.debug(`${fileContext}.getListOfAvailableSmsNumbers: ${numbers}`);
            this.availableNumbersCache.set(this._connectionId, numbers);
        }
        return numbers
    }

    public async getAvailableSmsNumberOptions(currentNumber?: string): Promise<DropdownItem[]> {
        return this.getListOfAvailableSmsNumbers().then(numbers => {
            let dropdownItems: DropdownItem[] = numbers.map(number => {
                return {
                    label: number,
                    value: number,
                }
            });
            if (currentNumber) {
                dropdownItems.unshift(this.getCurrentNumberOption(currentNumber));
            }
            dropdownItems.unshift(this.getNoneOption());
            return dropdownItems;
        });
    }

    getCurrentNumberOption(currentNumber): DropdownItem {
        return {
            label: currentNumber,
            value: currentNumber,
            badges: [{ label: "CURRENT", color: 'blue' }],
            hideBadgeOnSelection: true
        };
    }

    private getNoneOption(): DropdownItem {
        return {
            label: this.translate.instant('users_right_panel.quickAdd.none'),
            value: null
        };
    }

    /**
     * @param accountId
     * @returns List of users which have smsIntegration.number in their user tags
     */
    async getListOfUsersWithSmsNumberAssigned(accountId: string): Promise<User[]> {
        return this.adminService.getUsersByAccountIdWithoutSpinner(accountId, { $Expand: 'tags', $SearchKeys: 'tags', $Search: 'smsIntegration%20number' });
    }

    /**
     * Get the connectionId from the Twilio account tags. This shouldn't add and latency since the AccountTags are cached by the admin service.
     * @param accountId
     * @returns the connectionId from the Twilio account tags
     */
    async getTwilioConnectionIdFromTags(accountId: string): Promise<string> {
        return this.adminService.getAccountTags(accountId).then(tags => tags?.Twilio?.connectionId);
    }
}
